import {
  openDB,
  DBSchema,
  IDBPDatabase,
  StoreNames,
  StoreKey,
  IndexNames,
} from 'idb';

import { Logger } from '../logger';

import { InMemoryIdbService, InMemoryRow } from './in-memory-idb.service';

const IDB_ERROR_OPTIONS = {
  tags: {
    vkdoc_error_type: 'idb',
  },
} as const;

const isIndexedDBAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  'indexedDB' in window &&
  window.indexedDB != null;

const TIMESTAMP_INDEX = 'timestamp';

// eslint-disable-next-line no-magic-numbers
const DEFAULT_CACHE_EXPIRATION_DURATION = 1000 * 60 * 60 * 24 * 14;

/**
 * Базовый абстрактный сервис для кэш-сервисов на IndexedDB.
 *
 * Открытие БД с переподключением при обрыве соединения.
 * Очистка протухших записей и обёртку с логированием ошибок.
 * Наследники задают `database`/`tableName` и реализуют чтение/запись под свою
 * доменную логику, переиспользуя унаследованный `init`.
 */
export abstract class IdbCacheService<
  Schema extends DBSchema,
  TableName extends StoreNames<Schema>,
  SavedValue = unknown,
  GetResult = unknown,
> {
  protected db: IDBPDatabase<Schema> | null = null;

  protected memoryDb: InMemoryIdbService<Schema, TableName> | null = null;

  private initPromise: Promise<IDBPDatabase<Schema> | null> | null = null;

  protected abstract readonly database: string;

  protected abstract readonly tableName: TableName;

  protected readonly version: number = 1;

  protected readonly expirationDuration: number =
    DEFAULT_CACHE_EXPIRATION_DURATION;

  constructor(protected readonly logger: Logger) {}

  abstract getValue(...args: unknown[]): Promise<GetResult | null>;

  abstract saveValue(
    value: SavedValue,
    ...args: unknown[]
  ): Promise<IDBValidKey | null>;

  async init(
    tableName: TableName = this.tableName,
  ): Promise<IDBPDatabase<Schema> | null> {
    if (this.db) {
      return this.db;
    }

    if (this.memoryDb) {
      return null;
    }

    if (!isIndexedDBAvailable()) {
      this.memoryDb = new InMemoryIdbService<Schema, TableName>();
      this.logger.warning(
        'IndexedDB недоступен, используется in-memory кэш (не переживает перезагрузку)',
        IDB_ERROR_OPTIONS,
      );
      return null;
    }

    if (!this.initPromise) {
      this.initPromise = this.openDatabase(tableName).finally(() => {
        this.initPromise = null;
      });
    }

    return this.initPromise;
  }

  private async openDatabase(
    tableName: TableName,
  ): Promise<IDBPDatabase<Schema> | null> {
    try {
      this.db = await openDB<Schema>(this.database, this.version, {
        upgrade: (db) => {
          if (!db.objectStoreNames.contains(tableName)) {
            const store = db.createObjectStore(tableName, {
              keyPath: 'id',
            });
            store.createIndex(
              TIMESTAMP_INDEX as IndexNames<Schema, TableName>,
              TIMESTAMP_INDEX,
            );
          }
        },
      });

      this.db.onclose = () => {
        this.db = null;
      };
      this.db.onversionchange = () => {
        this.db?.close();
        this.db = null;
      };

      await this.clearTableIfExpired(tableName);

      return this.db;
    } catch (error) {
      this.logger.error(error, IDB_ERROR_OPTIONS);
      this.db = null;
      return null;
    }
  }

  protected async writeRecord(
    tableName: TableName,
    value: InMemoryRow<Schema, TableName>,
  ): Promise<StoreKey<Schema, TableName> | null> {
    const db = await this.init(tableName);

    if (this.memoryDb) {
      return this.memoryDb.put(String(tableName), value);
    }

    if (!db) {
      return null;
    }

    const store = db.transaction(tableName, 'readwrite').objectStore(tableName);
    return (await store.put(value)) ?? null;
  }

  async clearTableIfExpired(tableName: TableName = this.tableName) {
    try {
      const db = await this.init(tableName);

      if (this.memoryDb) {
        const threshold = Date.now() - this.expirationDuration;
        if (this.memoryDb.hasExpired(String(tableName), threshold)) {
          this.memoryDb.clear(String(tableName));
        }
        return null;
      }

      if (!db) {
        return null;
      }

      const tx = db.transaction(tableName, 'readwrite');
      const range = IDBKeyRange.upperBound(
        Date.now() - this.expirationDuration,
      );
      const cursor = await tx
        .objectStore(tableName)
        .index(TIMESTAMP_INDEX as IndexNames<Schema, TableName>)
        .openCursor(range);

      if (cursor) {
        await db.clear(tableName);
      }
      return null;
    } catch (error) {
      this.logger.error(error, IDB_ERROR_OPTIONS);
      return null;
    }
  }

  async clearTable(tableName: TableName = this.tableName) {
    try {
      const db = await this.init(tableName);

      if (this.memoryDb) {
        this.memoryDb.clear(String(tableName));
        return null;
      }

      return (await db?.clear(tableName)) ?? null;
    } catch (error) {
      this.logger.error(error, IDB_ERROR_OPTIONS);
      return null;
    }
  }

  async deleteValue(
    id: StoreKey<Schema, TableName>,
    tableName: TableName = this.tableName,
  ) {
    try {
      const db = await this.init(tableName);

      if (this.memoryDb) {
        this.memoryDb.delete(String(tableName), id);
        return null;
      }

      const tx = db?.transaction(tableName, 'readwrite');
      const store = tx?.objectStore(tableName);
      return await store?.delete(id);
    } catch (error) {
      this.logger.error(error, IDB_ERROR_OPTIONS);
      return null;
    }
  }
}
